import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Sparkles, X } from 'lucide-react';
interface TopicEditorProps {
  /** Unique per instance — keeps the autocomplete datalist ids from colliding. */
  instanceId: string;
  heading: string;
  activeTerms: string[];
  /** Terms the system pulled out of the notes, used to show provenance. */
  detectedTerms: string[];
  suggestedTerms: string[];
  suggestedLabel: string;
  vocabulary: string[];
  onToggleTerm: (term: string) => void;
  emptyHint?: string;
  footer?: React.ReactNode;
}
export function TopicEditor({
  instanceId,
  heading,
  activeTerms,
  detectedTerms,
  suggestedTerms,
  suggestedLabel,
  vocabulary,
  onToggleTerm,
  emptyHint = 'Nothing selected yet — pick a topic below.',
  footer
}: TopicEditorProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const listId = `topic-vocabulary-${instanceId}`;
  const unusedVocabulary = vocabulary.filter(term => !activeTerms.includes(term) && !suggestedTerms.includes(term));
  const hasCustomTerms = activeTerms.some(term => !detectedTerms.includes(term));
  const commitDraft = () => {
    const value = draft.trim().toLowerCase();
    if (value && !activeTerms.includes(value)) onToggleTerm(value);
    setDraft('');
    setIsAdding(false);
  };
  return <div data-id="element-1947" data-mp="27kn4c17sd32n">
      <div className="flex items-baseline justify-between gap-3" data-id="element-1948" data-mp="2lhs9lyzodxwu">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D]" data-id="element-1949" data-mp="2lg7bjze5jwu8">
          {heading}
        </div>
        <span className="text-xs text-gray-500" data-id="element-1950" data-mp="2v7vbypsoso7u">
          {activeTerms.length} selected
        </span>
      </div>

      <motion.div layout className="mt-2 flex flex-wrap gap-1.5" data-id="element-1951" data-mp="2q90gan9mf56b">
        <AnimatePresence initial={false} data-id="element-1952" data-mp="2qav3k88d79dk">
          {activeTerms.map(term => {
          const detected = detectedTerms.includes(term);
          return <motion.button key={term} type="button" layout onClick={() => onToggleTerm(term)} title={detected ? 'Detected in your notes — click to remove' : 'Added by you — click to remove'} initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }} className={`inline-flex items-center gap-1 rounded py-1 pl-2 pr-1.5 text-xs font-medium transition-colors ${detected ? 'bg-white text-[#30718D] hover:bg-gray-50' : 'bg-[#30718D] text-white hover:bg-[#265a70]'}`} data-id="element-1953" data-mp="2zarn5vfwdi1r">
                {detected && <Sparkles size={11} aria-hidden="true" data-id="element-1954" data-mp="v89k7yqt374s" />}
                {term}
                <X size={12} aria-hidden="true" data-id="element-1955" data-mp="z9bo4svo47d3" />
              </motion.button>;
        })}
        </AnimatePresence>

        {activeTerms.length === 0 && <span className="text-xs text-gray-600" data-id="element-1956" data-mp="myaraxzayj24">{emptyHint}</span>}
      </motion.div>

      {suggestedTerms.length > 0 && <motion.div layout className="mt-4" data-id="element-1957" data-mp="hzr835c3fldg">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1958" data-mp="dxcpmmcc6nmp">
            {suggestedLabel}
          </div>
          <motion.div layout className="mt-2 flex flex-wrap items-center gap-1.5" data-id="element-1959" data-mp="1anvbtlb3todi">
            <AnimatePresence initial={false} data-id="element-1960" data-mp="15pttj2li5t33">
              {suggestedTerms.map(term => <motion.button key={term} type="button" layout onClick={() => onToggleTerm(term)} initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }} className="inline-flex items-center gap-1 rounded border border-dashed border-[#30718D]/40 bg-white py-1 pl-1.5 pr-2 text-xs font-medium text-[#30718D] transition-colors hover:border-[#30718D] hover:bg-[#30718D]/5" data-id="element-1961" data-mp="11lm3n23b673a">
                  <Plus size={12} aria-hidden="true" data-id="element-1962" data-mp="1460no2mmo2sx" />
                  {term}
                </motion.button>)}
            </AnimatePresence>
          </motion.div>
        </motion.div>}

      <motion.div layout className="mt-4" data-id="element-1963" data-mp="1wvheivulnbn1">
        {isAdding ? <div className="flex flex-wrap items-center gap-2" data-id="element-1964" data-mp="1wslft5axgixj">
            <input autoFocus list={listId} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitDraft();
          }
          if (event.key === 'Escape') {
            setDraft('');
            setIsAdding(false);
          }
        }} placeholder="e.g. psychological safety" aria-label="Add a topic" className="w-56 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1965" data-mp="1iueq4cg4tp8y" />
            <datalist id={listId} data-id="element-1966" data-mp="3hcqhk5k81p05">
              {unusedVocabulary.map(term => <option key={term} value={term} data-id="element-1967" data-mp="3cdx7kn458g4q" />)}
            </datalist>
            <button type="button" onClick={commitDraft} className="text-xs font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1968" data-mp="3cc2jx11hm9wy">
              Add
            </button>
            <button type="button" onClick={() => {
          setDraft('');
          setIsAdding(false);
        }} className="text-xs font-medium text-gray-500 transition-colors hover:text-gray-900" data-id="element-1969" data-mp="2ylqq8xp9833p">
              Cancel
            </button>
          </div> : <button type="button" onClick={() => setIsAdding(true)} className="inline-flex items-center gap-1 text-xs font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1970" data-mp="12hjbtl7y04j1">
            <Plus size={13} data-id="element-1971" data-mp="elon3q8idh6h" />
            Add topic
          </button>}
      </motion.div>

      {footer}

      {hasCustomTerms && <p className="mt-3 text-[11px] leading-relaxed text-gray-500" data-id="element-1972" data-mp="jlb1w43zj5he">
          Chips with a spark came from your notes. Solid teal chips you added
          yourself.
        </p>}
    </div>;
}